/**
 * Web Audio engine for BEAT-LAB synth playground.
 * Plays notes using a preset's parameters (waveform, drive, filter, ADSR, FX).
 */
let ctx = null;
let masterGain = null;
let convolver = null;
let convolverGain = null;
let dryGain = null;
let delayNode = null;
let delayGain = null;
let delayFb = null;
let analyser = null;

function ensureCtx() {
  if (ctx) return ctx;
  // eslint-disable-next-line no-undef
  const AC = window.AudioContext || window.webkitAudioContext;
  ctx = new AC();
  masterGain = ctx.createGain();
  masterGain.gain.value = 0.6;

  analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.85;

  // Build a simple reverb via algorithmic impulse
  convolver = ctx.createConvolver();
  convolver.buffer = makeImpulse(ctx, 2.2, 2.4);
  convolverGain = ctx.createGain();
  convolverGain.gain.value = 0.0;
  dryGain = ctx.createGain();
  dryGain.gain.value = 1.0;

  // Delay
  delayNode = ctx.createDelay(2.0);
  delayNode.delayTime.value = 0.28;
  delayGain = ctx.createGain();
  delayGain.gain.value = 0.0;
  delayFb = ctx.createGain();
  delayFb.gain.value = 0.35;

  delayNode.connect(delayFb).connect(delayNode);
  delayNode.connect(delayGain);

  // Routing: voices -> [dryGain + convolverGain + delayGain] -> master -> analyser -> destination
  dryGain.connect(masterGain);
  convolverGain.connect(masterGain);
  delayGain.connect(masterGain);
  convolver.connect(convolverGain);
  masterGain.connect(analyser);
  analyser.connect(ctx.destination);

  return ctx;
}

function makeImpulse(audioCtx, duration, decay) {
  const rate = audioCtx.sampleRate;
  const length = rate * duration;
  const impulse = audioCtx.createBuffer(2, length, rate);
  for (let c = 0; c < 2; c++) {
    const data = impulse.getChannelData(c);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
    }
  }
  return impulse;
}

function makeDistortion(audioCtx, amount) {
  // amount 0..1 -> waveshaper curve
  const k = amount * 100;
  const n = 1024;
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i * 2) / n - 1;
    curve[i] = ((3 + k) * x * 20 * (Math.PI / 180)) / (Math.PI + k * Math.abs(x));
  }
  const ws = audioCtx.createWaveShaper();
  ws.curve = curve;
  ws.oversample = "4x";
  return ws;
}

function makeBitcrusher(audioCtx, bits, normFreq) {
  // Simple bitcrusher via ScriptProcessor (deprecated but ubiquitous)
  const bufSize = 4096;
  const node = audioCtx.createScriptProcessor(bufSize, 1, 1);
  let phaser = 0;
  let last = 0;
  const step = Math.pow(0.5, bits);
  node.onaudioprocess = (e) => {
    const input = e.inputBuffer.getChannelData(0);
    const output = e.outputBuffer.getChannelData(0);
    for (let i = 0; i < bufSize; i++) {
      phaser += normFreq;
      if (phaser >= 1.0) {
        phaser -= 1.0;
        last = step * Math.floor(input[i] / step + 0.5);
      }
      output[i] = last;
    }
  };
  return node;
}

/**
 * Play a single note using the given preset for the given duration.
 * preset: parameters from server
 * midiNote: e.g. 60 = middle C
 * duration: seconds (note hold time)
 */
export function playNote(preset, midiNote, duration = 0.5) {
  const c = ensureCtx();
  if (c.state === "suspended") c.resume();

  const now = c.currentTime;
  const baseFreq = 440 * Math.pow(2, (midiNote - 69 + (preset.pitch_semitones || 0)) / 12);
  const detuneCents = preset.detune_cents || 0;

  // Oscillators (main + detuned + sub)
  const oscMain = c.createOscillator();
  oscMain.type = preset.waveform === "noise" ? "sawtooth" : (preset.waveform || "sawtooth");
  oscMain.frequency.setValueAtTime(baseFreq, now);

  const oscDet = c.createOscillator();
  oscDet.type = oscMain.type;
  oscDet.frequency.setValueAtTime(baseFreq, now);
  oscDet.detune.setValueAtTime(detuneCents, now);

  const oscSub = c.createOscillator();
  oscSub.type = "sine";
  const subMult = preset.sub_octave ? Math.pow(2, preset.sub_octave) : 1;
  oscSub.frequency.setValueAtTime(baseFreq * subMult, now);

  const oscGain = c.createGain();
  oscGain.gain.value = 0;

  // LFO modulating filter cutoff
  const lfo = c.createOscillator();
  lfo.frequency.setValueAtTime(preset.lfo_rate || 1, now);
  const lfoGain = c.createGain();
  lfoGain.gain.value = (preset.lfo_depth || 0) * 1200;

  // Filter
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  const cutoffHz = 80 + (preset.filter_cutoff || 0.5) * 11000;
  filter.frequency.setValueAtTime(cutoffHz, now);
  filter.Q.setValueAtTime((preset.filter_resonance || 0) * 18, now);

  lfo.connect(lfoGain).connect(filter.frequency);

  // Glide handled by glide_ms (not actually rebinding here; placeholder)

  // Distortion / saturation / bitcrush
  const dist = makeDistortion(c, (preset.drive || 0) * 0.9 + (preset.saturation || 0) * 0.4);
  let crusher = null;
  if ((preset.bitcrush || 0) > 0.01) {
    const bits = 16 - Math.round((preset.bitcrush || 0) * 12); // 16 -> 4 bits
    const normFreq = 0.15 + (preset.bitcrush || 0) * 0.6;
    crusher = makeBitcrusher(c, bits, normFreq);
  }

  // Stereo width via panner duplicated
  const splitter = c.createGain();
  const widthGain = c.createGain();
  widthGain.gain.value = 1;

  // Voice routing
  oscMain.connect(oscGain);
  oscDet.connect(oscGain);
  oscSub.connect(oscGain);
  let chain = oscGain.connect(filter);
  chain = chain.connect(dist);
  if (crusher) chain = chain.connect(crusher);
  chain.connect(splitter);
  splitter.connect(widthGain);
  widthGain.connect(dryGain);
  if ((preset.reverb || 0) > 0.001) {
    convolverGain.gain.setValueAtTime(preset.reverb || 0, now);
    splitter.connect(convolver);
  }
  if ((preset.delay || 0) > 0.001) {
    delayGain.gain.setValueAtTime(preset.delay || 0, now);
    splitter.connect(delayNode);
  }

  // ADSR envelope
  const A = Math.max(0.002, (preset.attack || 0) * 1.2);
  const D = Math.max(0.01, (preset.decay || 0) * 1.2);
  const S = Math.min(1, Math.max(0, preset.sustain ?? 0.6));
  const R = Math.max(0.02, (preset.release || 0.2) * 1.5);
  const peak = 0.9;
  oscGain.gain.cancelScheduledValues(now);
  oscGain.gain.setValueAtTime(0, now);
  oscGain.gain.linearRampToValueAtTime(peak, now + A);
  oscGain.gain.linearRampToValueAtTime(peak * S, now + A + D);
  const releaseStart = now + A + D + Math.max(0.05, duration);
  oscGain.gain.setValueAtTime(peak * S, releaseStart);
  oscGain.gain.linearRampToValueAtTime(0.0001, releaseStart + R);

  // Start/stop
  oscMain.start(now);
  oscDet.start(now);
  oscSub.start(now);
  lfo.start(now);
  const stopAt = releaseStart + R + 0.05;
  oscMain.stop(stopAt);
  oscDet.stop(stopAt);
  oscSub.stop(stopAt);
  lfo.stop(stopAt);
}

/**
 * Play a punchy 808 with custom parameters
 */
export function play808({
  pitch = 36,
  duration = 0.7,
  drive = 0.5,
  glide = 0.15,
  punch = 0.6,
  sub = 0.8,
  saturation = 0.4,
}) {
  const c = ensureCtx();
  if (c.state === "suspended") c.resume();
  const now = c.currentTime;
  const freq = 440 * Math.pow(2, (pitch - 69) / 12);

  const osc = c.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq * 3, now);
  osc.frequency.exponentialRampToValueAtTime(freq, now + Math.max(0.01, glide));

  const gain = c.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.95 * (0.6 + sub * 0.6), now + 0.003);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  // Click for punch
  const click = c.createOscillator();
  click.type = "triangle";
  click.frequency.setValueAtTime(160, now);
  const clickGain = c.createGain();
  clickGain.gain.setValueAtTime(0.0, now);
  clickGain.gain.linearRampToValueAtTime(0.6 * punch, now + 0.002);
  clickGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);

  const dist = makeDistortion(c, drive * 0.8 + saturation * 0.4);

  osc.connect(gain).connect(dist).connect(dryGain);
  click.connect(clickGain).connect(dryGain);

  osc.start(now);
  click.start(now);
  osc.stop(now + duration + 0.1);
  click.stop(now + 0.1);
}

export function getAnalyser() {
  ensureCtx();
  return analyser;
}

export function setMasterVolume(v) {
  ensureCtx();
  masterGain.gain.value = Math.max(0, Math.min(1, v));
}
